import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { PlusIcon, SearchIcon, WalletIcon, TrendingDownIcon, TrendingUpIcon } from 'lucide-react';
import { Button } from '../components/Button';
import { PayoutCard, Payout } from '../components/PayoutCard';
import { EarningCard, Earning } from '../components/EarningCard';
import { AddPayoutModal } from '../components/AddPayoutModal';
import { EditPayoutModal } from '../components/EditPayoutModal';
import { AddEarningModal } from '../components/AddEarningModal';
import { EditEarningModal } from '../components/EditEarningModal';
interface PayoutsProps {
  payouts: Payout[];
  earnings: Earning[];
  onAddPayout: (payout: Omit<Payout, 'id'>) => void;
  onEditPayout: (payout: Payout) => void;
  onDeletePayout: (payout: Payout) => void;
  onAddEarning: (earning: Omit<Earning, 'id'>) => void;
  onEditEarning: (earning: Earning) => void;
  onDeleteEarning: (earning: Earning) => void;
  customers: Array<{
    id: string;
    customerId: string;
    name: string;
  }>;
}
export function Payouts({
  payouts,
  earnings,
  onAddPayout,
  onEditPayout,
  onDeletePayout,
  onAddEarning,
  onEditEarning,
  onDeleteEarning,
  customers
}: PayoutsProps) {
  const [isAddPayoutModalOpen, setIsAddPayoutModalOpen] = useState(false);
  const [isAddEarningModalOpen, setIsAddEarningModalOpen] = useState(false);
  const [editingPayout, setEditingPayout] = useState<Payout | null>(null);
  const [editingEarning, setEditingEarning] = useState<Earning | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const isReadOnly = false;
  // Filter payouts
  const filteredPayouts = useMemo(() => {
    if (!searchQuery.trim()) return payouts;
    const query = searchQuery.toLowerCase();
    return payouts.filter(payout => payout.notes?.toLowerCase().includes(query) || payout.customerName?.toLowerCase().includes(query));
  }, [payouts, searchQuery]);
  // Filter earnings
  const filteredEarnings = useMemo(() => {
    if (!searchQuery.trim()) return earnings;
    const query = searchQuery.toLowerCase();
    return earnings.filter(earning => earning.notes?.toLowerCase().includes(query) || earning.type.toLowerCase().includes(query) || earning.customerName?.toLowerCase().includes(query));
  }, [earnings, searchQuery]);
  // Calculate stats
  const totalPayouts = payouts.reduce((sum, payout) => sum + payout.amount, 0);
  const totalEarnings = earnings.reduce((sum, earning) => sum + earning.amount, 0);
  const netAmount = totalEarnings - totalPayouts;
  return <div className="space-y-6" data-id="element-1712" data-mp="1yvfcqxzq6hbt">
      {/* Header */}
      <header className="flex items-center justify-between" data-id="element-1713" data-mp="1ysjgde3j00ph">
        <div data-id="element-1714" data-mp="23uiikknj4rku">
          <h1 className="text-4xl font-bold text-stone-900 mb-2" data-id="element-1715" data-mp="23u8oolzd609v">
            Udbetalinger (Intern)
          </h1>
          <p className="text-stone-600" data-id="element-1716" data-mp="1oyjcffjzd1ic">
            Administrer dine interne udbetalinger og indtjeninger
          </p>
        </div>
        <div className="flex gap-3" data-id="element-1717" data-mp="1a9hm0b1zt3mr">
          <Button onClick={() => setIsAddEarningModalOpen(true)} variant="primary" className="flex items-center gap-2" data-id="element-1718" data-mp="1aaj0kpi7w898">
            <PlusIcon className="w-5 h-5" data-id="element-1719" data-mp="1k8go1as9610s" />
            Tilføj indtjening
          </Button>
          <Button onClick={() => setIsAddPayoutModalOpen(true)} variant="secondary" className="flex items-center gap-2" data-id="element-1720" data-mp="1f944aywoha2f">
            <PlusIcon className="w-5 h-5" data-id="element-1721" data-mp="1t6iy0ftys407" />
            Tilføj udbetaling
          </Button>
        </div>
      </header>

      {/* Stats */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.1
    }} className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-1722" data-mp="22yyflc1cmqkk">
        <div className="bg-white rounded-xl p-6 border border-stone-200 shadow-sm" data-id="element-1723" data-mp="27xi33gvlcaf1">
          <div className="flex items-center gap-3 mb-2" data-id="element-1724" data-mp="27voqlkyaqvz4">
            <div className="p-2 bg-emerald-100 rounded-lg" data-id="element-1725" data-mp="5a2xsj91nh97">
              <TrendingUpIcon className="w-5 h-5 text-emerald-700" data-id="element-1726" data-mp="a9nzh1flm3pm" />
            </div>
            <p className="text-sm font-medium text-stone-600" data-id="element-1727" data-mp="f0nug155qbph">
              Total indtjeninger
            </p>
          </div>
          <p className="text-3xl font-bold text-emerald-700" data-id="element-1728" data-mp="k0aa0h8gk41g">
            {totalEarnings.toLocaleString('da-DK')} kr
          </p>
          <p className="text-sm text-stone-500 mt-1" data-id="element-1729" data-mp="3iixlsl195pzt">
            {earnings.length} indtjeninger
          </p>
        </div>

        <div className="bg-white rounded-xl p-6 border border-stone-200 shadow-sm" data-id="element-1730" data-mp="3nik0kywqbeaq">
          <div className="flex items-center gap-3 mb-2" data-id="element-1731" data-mp="3se90j3cnux7k">
            <div className="p-2 bg-red-100 rounded-lg" data-id="element-1732" data-mp="19j1fhwaoho4">
              <TrendingDownIcon className="w-5 h-5 text-red-700" data-id="element-1733" data-mp="16wiyn7v8hr4" />
            </div>
            <p className="text-sm font-medium text-stone-600" data-id="element-1734" data-mp="17p1uemft3i3o">
              Total udbetalinger
            </p>
          </div>
          <p className="text-3xl font-bold text-red-700" data-id="element-1735" data-mp="2x6guxsydqc6s">
            {totalPayouts.toLocaleString('da-DK')} kr
          </p>
          <p className="text-sm text-stone-500 mt-1" data-id="element-1736" data-mp="2s73xyfdsnoqh">
            {payouts.length} udbetalinger
          </p>
        </div>

        <div className="bg-white rounded-xl p-6 border border-stone-200 shadow-sm" data-id="element-1737" data-mp="2nfcgcucm8tch">
          <div className="flex items-center gap-3 mb-2" data-id="element-1738" data-mp="2igj2v1xbx2cb">
            <div className={`p-2 rounded-lg ${netAmount >= 0 ? 'bg-teal-100' : 'bg-amber-100'}`} data-id="element-1739" data-mp="2dihk6i3tf51d">
              <WalletIcon className={`w-5 h-5 ${netAmount >= 0 ? 'text-teal-700' : 'text-amber-700'}`} data-id="element-1740" data-mp="29gcshowxgihd" />
            </div>
            <p className="text-sm font-medium text-stone-600" data-id="element-1741" data-mp="24jvt1yka2hoz">Netto</p>
          </div>
          <p className={`text-3xl font-bold ${netAmount >= 0 ? 'text-teal-700' : 'text-amber-700'}`} data-id="element-1742" data-mp="1zk9gzsg6r7hr">
            {netAmount.toLocaleString('da-DK')} kr
          </p>
          <p className="text-sm text-stone-500 mt-1" data-id="element-1743" data-mp="3ty0vb59gmfjj">
            Indtjeninger - Udbetalinger
          </p>
        </div>
      </motion.div>

      {/* Search */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.2
    }} className="relative" data-id="element-1744" data-mp="1wys99ky4m9a3">
        <SearchIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" data-id="element-1745" data-mp="1n8z0ssta6aa2" />
        <input type="text" placeholder="Søg i noter, type eller kunde..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-12 pr-4 py-3 rounded-xl border border-stone-200 bg-white text-stone-900 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all" data-id="element-1746" data-mp="2aykz7e1twlh0" />
      </motion.div>

      {/* Earnings Section */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.3
    }} data-id="element-1747" data-mp="uk1s9hevfrui">
        <div className="flex items-center justify-between mb-4" data-id="element-1748" data-mp="33roea9aszxqh">
          <h2 className="text-2xl font-semibold text-stone-900" data-id="element-1749" data-mp="3hrz5xejxtgcp">
            Indtjeninger
          </h2>
          <span className="text-sm text-stone-500" data-id="element-1750" data-mp="3dq47qixazj35">
            {filteredEarnings.length} indtjeninger
          </span>
        </div>

        {filteredEarnings.length === 0 ? <div className="bg-white rounded-xl p-12 text-center border border-stone-100" data-id="element-1751" data-mp="2l1ym7tyfdq29">
            <TrendingUpIcon className="w-12 h-12 text-stone-300 mx-auto mb-4" data-id="element-1752" data-mp="2l05e0a85yxs5" />
            <h3 className="text-lg font-medium text-stone-900 mb-2" data-id="element-1753" data-mp="2yuo9s2tlr31h">
              {searchQuery ? 'Ingen indtjeninger matcher din søgning' : 'Ingen indtjeninger endnu'}
            </h3>
            <p className="text-stone-500 mb-6" data-id="element-1754" data-mp="26appl0w3v4fm">
              {searchQuery ? 'Prøv at justere din søgning' : 'Tilføj din første indtjening for at komme i gang'}
            </p>
            {!searchQuery && <Button onClick={() => setIsAddEarningModalOpen(true)} data-id="element-1755" data-mp="2w4ktprvauzut">
                Tilføj indtjening
              </Button>}
          </div> : <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-1756" data-mp="35612xwadovxg">
            {filteredEarnings.map((earning, index) => <EarningCard key={earning.id} earning={earning} index={index} onEdit={setEditingEarning} onDelete={onDeleteEarning} readOnly={isReadOnly} data-id="element-1757" data-mp="3a42hq50ru934" data-mpi={index} />)}
          </div>}
      </motion.div>

      {/* Payouts Section */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.4
    }} data-id="element-1758" data-mp="3svn0kfb3r33c">
        <div className="flex items-center justify-between mb-4" data-id="element-1759" data-mp="bts8csl5mj6b">
          <h2 className="text-2xl font-semibold text-stone-900" data-id="element-1760" data-mp="br61ywa029po">
            Udbetalinger
          </h2>
          <span className="text-sm text-stone-500" data-id="element-1761" data-mp="6win7p4sstoa">
            {filteredPayouts.length} udbetalinger
          </span>
        </div>

        {filteredPayouts.length === 0 ? <div className="bg-white rounded-xl p-12 text-center border border-stone-100" data-id="element-1762" data-mp="3u0opk84i67m4">
            <WalletIcon className="w-12 h-12 text-stone-300 mx-auto mb-4" data-id="element-1763" data-mp="uj8nakxl6e69" />
            <h3 className="text-lg font-medium text-stone-900 mb-2" data-id="element-1764" data-mp="pkdwavpgtjb2">
              {searchQuery ? 'Ingen udbetalinger matcher din søgning' : 'Ingen udbetalinger endnu'}
            </h3>
            <p className="text-stone-500 mb-6" data-id="element-1765" data-mp="grk8g9vydbll">
              {searchQuery ? 'Prøv at justere din søgning' : 'Tilføj din første udbetaling for at komme i gang'}
            </p>
            {!searchQuery && <Button onClick={() => setIsAddPayoutModalOpen(true)} variant="secondary" data-id="element-1766" data-mp="ti7h568cxsw8">
                Tilføj udbetaling
              </Button>}
          </div> : <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-1767" data-mp="12j5lxqqme4fv">
            {filteredPayouts.map((payout, index) => <PayoutCard key={payout.id} payout={payout} index={index} onEdit={setEditingPayout} onDelete={onDeletePayout} readOnly={isReadOnly} data-id="element-1768" data-mp="xihn4iw5gjb3" data-mpi={index} />)}
          </div>}
      </motion.div>

      <AddPayoutModal isOpen={isAddPayoutModalOpen} onClose={() => setIsAddPayoutModalOpen(false)} onAdd={onAddPayout} customers={customers} data-id="element-1769" data-mp="1lb7x4mkhssiq" />

      <EditPayoutModal isOpen={!!editingPayout} onClose={() => setEditingPayout(null)} onSave={onEditPayout} payout={editingPayout} customers={customers} data-id="element-1770" data-mp="1v7uev2hiv3gj" />

      <AddEarningModal isOpen={isAddEarningModalOpen} onClose={() => setIsAddEarningModalOpen(false)} onAdd={onAddEarning} customers={customers} data-id="element-1771" data-mp="29v1livzz5f9m" />

      <EditEarningModal isOpen={!!editingEarning} onClose={() => setEditingEarning(null)} onSave={onEditEarning} earning={editingEarning} customers={customers} data-id="element-1772" data-mp="1zvkcc4likj8w" />
    </div>;
}